/**
 * Derive numbered, risk-tiered citations from assistant markdown.
 *
 * The composer emits inline Markdown links (`[Title](URL)`). For risk-tiered
 * citations we:
 *   - assign a stable `id` by order of first appearance in the text,
 *   - deduplicate by canonical URL (one number per unique source),
 *   - enrich each with the risk tier / publisher from the backend `citations`
 *     payload (looked up by canonical URL; missing → N/A).
 *
 * This is a pure function so the marker renderer (`markdown.ts`) and the
 * reference list (`CitationsPanel`) derive identical numbering from the same
 * inputs.
 */

import type { Citation, RawCitation } from '@/types'
import { parseImpactToTier } from './riskTier'

/** Matches inline Markdown links, skipping images (`![alt](url)`). */
export const LINK_RE = /(!?)\[([^\]]+)\]\(\s*(<[^>]+>|[^()\s]+)(?:\s+(?:"[^"]*"|'[^']*'|\([^)]*\)))?\s*\)/g

/** MCP aggregate titles look like: `MCP Aggregate (Probe 1) — decay_summary — …`. */
const MCP_AGGREGATE_LABEL_RE = /^MCP\s+Aggregate\b/i
const MCP_PROBE_NUM_RE = /\(Probe\s+(\d+)\)/i

/**
 * Rewrite legacy MCP cites that used fragment/empty hrefs (`(#)`, `()`, …)
 * into stable synthetic `mcp://aggregate/probe/N` targets so they become
 * numbered reference markers (no click-through).
 */
export function normalizeMcpCitationLinks(content: string): string {
  if (!content) return content
  LINK_RE.lastIndex = 0
  return content.replace(LINK_RE, (match, bang: string, label: string, rawUrl: string) => {
    if (bang === '!') return match
    const text = String(label || '').trim()
    if (!MCP_AGGREGATE_LABEL_RE.test(text)) return match

    let url = String(rawUrl || '').trim()
    if (url.startsWith('<') && url.endsWith('>')) url = url.slice(1, -1).trim()

    // Already a proper synthetic MCP cite target — leave alone.
    if (/^mcp:\/\//i.test(url)) return match

    // Legacy / broken targets the model used when evidence had no URL.
    const isBroken =
      !url ||
      url === '#' ||
      url.startsWith('#') ||
      url === 'about:blank' ||
      url === '/'

    if (!isBroken) return match

    const probeMatch = text.match(MCP_PROBE_NUM_RE)
    const probeNum = probeMatch ? Math.max(1, parseInt(probeMatch[1], 10)) : 1
    const synthetic = `mcp://aggregate/probe/${probeNum - 1}`
    return `[${text}](${synthetic})`
  })
}

/**
 * Normalize a URL for dedup: lowercase host, drop scheme differences, trailing
 * slash, query and fragment. Mirrors the backend `canonicalize_url` intent
 * closely enough for marker↔source matching.
 *
 * Synthetic MCP cite targets (`mcp://aggregate/probe/N`) keep their scheme so
 * they never collide with http(s) hosts.
 */
export function canonicalizeUrl(url: string): string {
  if (!url) return ''
  let raw = url.trim()
  if (raw.startsWith('<') && raw.endsWith('>')) raw = raw.slice(1, -1).trim()
  if (!raw) return ''
  if (/^mcp:\/\//i.test(raw)) {
    try {
      const u = new URL(raw)
      const host = (u.host || '').toLowerCase()
      let path = u.pathname || '/'
      if (path.length > 1) path = path.replace(/\/+$/, '')
      return `mcp://${host}${path}`
    } catch {
      return raw.toLowerCase().replace(/[#?].*$/, '').replace(/\/+$/, '')
    }
  }
  try {
    const hasScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(raw)
    const u = new URL(hasScheme ? raw : `https://${raw}`)
    const host = u.host.toLowerCase()
    let path = u.pathname || '/'
    if (path.length > 1) path = path.replace(/\/+$/, '')
    return `${host}${path}`
  } catch {
    return raw.toLowerCase().replace(/[#?].*$/, '').replace(/\/+$/, '')
  }
}

/** Pick the higher-severity tier (larger number; null is lowest). */
function higherTier(a: number | null, b: number | null): number | null {
  if (a == null) return b
  if (b == null) return a
  return Math.max(a, b)
}

function rawTier(raw: RawCitation | undefined): number | null {
  if (!raw) return null
  if (raw.risk_tier != null) return raw.risk_tier
  return parseImpactToTier(raw.impact)
}

function hostToPublisher(url: string): string | undefined {
  try {
    const hasScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(url)
    const host = new URL(hasScheme ? url : `https://${url}`).host.toLowerCase()
    return host.replace(/^www\./, '') || undefined
  } catch {
    return undefined
  }
}

/** Build a canonical-URL → RawCitation lookup, keeping highest-severity tier. */
export function buildCitationLookup(raw?: RawCitation[] | null): Map<string, RawCitation> {
  const lookup = new Map<string, RawCitation>()
  if (!Array.isArray(raw)) return lookup
  for (const c of raw) {
    if (!c || typeof c.url !== 'string') continue
    const key = canonicalizeUrl(c.url)
    if (!key) continue
    const existing = lookup.get(key)
    if (!existing) {
      lookup.set(key, c)
    } else {
      const merged: RawCitation = {
        ...existing,
        risk_tier: higherTier(rawTier(existing), rawTier(c)),
        actions: [...(existing.actions ?? []), ...(c.actions ?? [])],
      }
      lookup.set(key, merged)
    }
  }
  return lookup
}

/**
 * Parse the answer text into an ordered list of numbered citations.
 *
 * @param content  Assistant markdown.
 * @param raw      Backend citations payload (for risk tier / publisher lookup).
 */
export function parseCitationsFromMarkdown(
  content: string,
  raw?: RawCitation[] | null,
): Citation[] {
  if (!content) return []
  const normalized = normalizeMcpCitationLinks(content)
  const lookup = buildCitationLookup(raw)

  const byCanonical = new Map<string, Citation>()
  const ordered: Citation[] = []
  let nextId = 1

  LINK_RE.lastIndex = 0
  let m: RegExpExecArray | null
  while ((m = LINK_RE.exec(normalized)) !== null) {
    const isImage = m[1] === '!'
    if (isImage) continue
    const text = m[2].trim()
    let url = m[3].trim()
    if (url.startsWith('<') && url.endsWith('>')) url = url.slice(1, -1).trim()
    if (!url || url.startsWith('#')) continue

    const key = canonicalizeUrl(url)
    if (!key) continue

    const rawHit = lookup.get(key)

    if (byCanonical.has(key)) {
      // Repeat citation of the same source: capture the action/tier pairing.
      const existing = byCanonical.get(key)!
      if (text) {
        existing.actions = existing.actions ?? []
        existing.actions.push({ title: text, riskTier: existing.riskTier })
      }
      continue
    }

    const tier = rawTier(rawHit)
    const actions: Citation['actions'] = []
    if (Array.isArray(rawHit?.actions) && rawHit!.actions!.length) {
      for (const a of rawHit!.actions!) {
        actions.push({ title: a.title || rawHit?.title || text, riskTier: a.risk_tier ?? null })
      }
    }

    const isMcp =
      /^mcp:\/\//i.test(url) ||
      (typeof rawHit?.source_type === 'string' && rawHit.source_type.toLowerCase() === 'mcp')

    const citation: Citation = {
      id: nextId++,
      title: (rawHit?.title || text || url).trim(),
      url,
      riskTier: tier,
      publisher: rawHit?.publisher || (isMcp ? 'MCP' : hostToPublisher(url)),
      sourceType: isMcp ? 'mcp' : (rawHit?.source_type ?? undefined),
      actions: actions.length ? actions : undefined,
    }
    byCanonical.set(key, citation)
    ordered.push(citation)
  }

  return ordered
}

/** Build a canonical-URL → numbered Citation map (for marker rendering). */
export function citationMapByUrl(citations: Citation[]): Map<string, Citation> {
  const map = new Map<string, Citation>()
  for (const c of citations) {
    const key = canonicalizeUrl(c.url)
    if (key) map.set(key, c)
  }
  return map
}
